import { useEffect, useMemo } from "react";
import Head from "next/head";

import { MostPopularQuery, MostPopularQueryVariables } from "types/api";
import { trackLandingPageView } from "utils/analytics";
import { AdTemplatesEnum, registerAdsPageView } from "utils/ads";
import { withData, useQuery, gql, UrqlPageType } from "utils/urql";
import usePagination from "utils/usePagination";
import PageLayout from "components/PageLayout";
import LandingLayout from "components/landing/LandingLayout";
import LandingTitle from "components/landing/LandingTitle";
import LandingRiver from "components/landing/LandingRiver";
import LandingRailAd from "components/ads/LandingRail";
import NonMeteredNudge from "components/NonMeteredNudge";
import { throwCorrectStatus } from "utils/error";

const MOST_POPULAR_QUERY = gql`
  ${LandingRiver.fragment}

  query MostPopularQuery(
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    mostPopular {
      id
      slug
      grapeshot {
        segments
      }
      river(first: $first, last: $last, after: $after, before: $before) {
        ...landingRiver
      }
    }
  }
`;

const useTrackPageView = ({
  slug,
  displayName,
  river: { edges },
  grapeshot,
}: MostPopularQuery["mostPopular"] & { displayName: string }) => {
  // Convert the edges to a string so we can compare it more reliably in the
  // useEffect dependencies.
  const memoizedEdges = useMemo(
    () => edges.map((edge) => edge?.node.id).join("-"),
    [edges]
  );

  // Rerun the effect if edges, displayName, or slug changes.
  useEffect(() => {
    // Track the pageview in GTM
    trackLandingPageView({ displayName });

    // Register the pageview in ads.js
    registerAdsPageView({
      channel: "landing",
      template: AdTemplatesEnum.landing_standard,
      globalTargeting: {
        title: slug,
        grapeshot_segments: grapeshot?.segments,
      },
    });
  }, [displayName, memoizedEdges, slug]); //eslint-disable-line react-hooks/exhaustive-deps
};

const MostPopularDisplay = ({
  data,
  fetching,
}: {
  data: MostPopularQuery;
  fetching: boolean;
}) => {
  // The displayName stored in the CuratedGroup model in Ollie is "Most Popular",
  // but in the page display and analytics we call it "Popular". Use the correct
  // displayName here instead of the displayName from the API.
  const displayName = "Popular";
  useTrackPageView({ ...data.mostPopular, displayName });

  return (
    <>
      <Head>
        <title>{displayName} - The Atlantic</title>
        <meta property="og:title" content={displayName} key="og:title" />
        <meta
          property="og:url"
          content={`/${data.mostPopular.slug}/`}
          key="og:url"
        />
        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta
          property="og:image"
          key="og:image"
          content="https://cdn.theatlantic.com/assets/static/a/theatlantic/img/lacroix-default-thumbnail.png"
        />
      </Head>
      <LandingLayout
        fetching={fetching}
        header={<LandingTitle>{displayName}</LandingTitle>}
        river={<LandingRiver {...data.mostPopular.river} />}
        sidebar={<LandingRailAd targetingPos="boxsticky" />}
      />
    </>
  );
};

const MostPopular: UrqlPageType = () => {
  const { after, before, first, last } = usePagination();

  const [{ error, data, fetching }] = useQuery<
    MostPopularQuery,
    MostPopularQueryVariables
  >({
    query: MOST_POPULAR_QUERY,
    variables: {
      after,
      before,
      first,
      last,
    },
  });

  // If we're done fetching and there is still no data or no river, it's a 404.
  if (!fetching && (!data || !data.mostPopular.river.edges.length)) {
    throwCorrectStatus({ error });
  }

  return (
    <PageLayout>
      {data && data.mostPopular && (
        <MostPopularDisplay data={data} fetching={fetching} />
      )}
      <NonMeteredNudge />
    </PageLayout>
  );
};

export default withData(MostPopular);
